import React, { useEffect, useRef, useState } from 'react';
import Gantt from 'frappe-gantt';

const GanttChart = ({ projects: projectsProp = [], milestones: milestonesProp = [] }) => {
  const ganttRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState(projectsProp);
  const [milestones, setMilestones] = useState(milestonesProp);
  const [loading, setLoading] = useState(true);

  // Fetch projects and milestones from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch projects
        console.log('Fetching projects from API...');
        const projectsRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/projectsmgnt');
        console.log('Projects response status:', projectsRes.status);
        const projectsData = projectsRes.ok ? await projectsRes.json() : [];
        console.log('Fetched projects:', projectsData);

        // Fetch milestones/project-tasks
        console.log('Fetching milestones from API...');
        const milestonesRes = await fetch('https://slateblue-mallard-430836.hostingersite.com/api/milestones');
        console.log('Milestones response status:', milestonesRes.status);
        const milestonesData = milestonesRes.ok ? await milestonesRes.json() : [];
        console.log('Fetched milestones:', milestonesData);

        setProjects(projectsData);
        setMilestones(milestonesData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    // Only fetch if no props provided
    if (projectsProp.length === 0 && milestonesProp.length === 0) {
      fetchData();
    } else {
      setProjects(projectsProp);
      setMilestones(milestonesProp);
      setLoading(false);
    }
  }, [projectsProp, milestonesProp]);

  // Convert projects and milestones to Gantt tasks
  useEffect(() => {
    console.log('Projects data:', projects);
    console.log('Milestones data:', milestones);

    const ganttTasks = [];

    // Add projects
    projects.forEach((project, index) => {
      console.log('Processing project:', project);
      const startDate = project.startDate ? new Date(project.startDate) : new Date();
      const endDate = project.dueDate ? new Date(project.dueDate) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      ganttTasks.push({
        id: `project-${project._id || project.id || index}`,
        name: `[PROJECT] ${project.title || project.name}`,
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0],
        progress: project.progress || 0,
        dependencies: '',
        type: 'project',
        data: project
      });
    });

    // Add milestones/project-tasks
    milestones.forEach((milestone, index) => {
      console.log('Processing milestone:', milestone);
      const dueDate = milestone.dueDate ? new Date(milestone.dueDate) : new Date();
      const startDate = new Date(dueDate.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days before due date

      ganttTasks.push({
        id: `milestone-${milestone._id || milestone.id || index}`,
        name: `[PROJECT-TASK] ${milestone.title || milestone.name}`,
        start: startDate.toISOString().split('T')[0],
        end: dueDate.toISOString().split('T')[0],
        progress: milestone.status === 'Completed' ? 100 : milestone.status === 'In Progress' ? 50 : 0,
        dependencies: '',
        type: 'milestone',
        data: milestone
      });
    });

    console.log('Generated tasks:', ganttTasks);
    setTasks(ganttTasks);
  }, [projects, milestones]);

  useEffect(() => {
    if (ganttRef.current && tasks.length > 0) {
      new Gantt(ganttRef.current, tasks, {
        view_mode: 'Week',
        date_format: 'YYYY-MM-DD',
        custom_popup_html: (task) => {
          const taskData = tasks.find(t => t.id === task.id);
          if (taskData && taskData.type === 'milestone') {
            return `
              <div style="padding: 8px; min-width: 200px;">
                <h5 style="margin: 0; font-size: 14px; color: #1e293b;">${task.name}</h5>
                <p style="margin: 4px 0; font-size: 12px; color: #6b7280;">${task.progress}% completed</p>
                <p style="margin: 2px 0; font-size: 11px; color: #9ca3af;">Status: ${taskData.data.status || 'Pending'}</p>
                ${taskData.data.createdDate ? `<p style="margin: 2px 0; font-size: 11px; color: #9ca3af;">…Created: ${taskData.data.createdDate}</p>` : ''}
                ${taskData.data.employeeName ? `<p style="margin: 2px 0; font-size: 11px; color: #9ca3af;">By: ${taskData.data.employeeName}</p>` : ''}
              </div>
            `;
          } else {
            return `
              <div style="padding: 8px; min-width: 200px;">
                <h5 style="margin: 0; font-size: 14px; color: #1e293b;">${task.name}</h5>
                <p style="margin: 4px 0; font-size: 12px; color: #6b7280;">${task.progress}% completed</p>
                ${taskData && taskData.data.status ? `<p style="margin: 2px 0; font-size: 11px; color: #9ca3af;">Status: ${taskData.data.status}</p>` : ''}
              </div>
            `;
          }
        }
      });
    }
  }, [tasks]);

  const containerStyle = {
    padding: '1rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '1rem',
    color: '#1e293b'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <h1 style={headerStyle}>Projects & Project-Tasks Timeline</h1>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          color: '#6b7280'
        }}>
          Loading timeline data...
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h1 style={headerStyle}>Projects & Project-Tasks Timeline</h1>
      <div style={{
        background: 'white',
        padding: '1rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '1rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          color: '#6b7280'
        }}>
          <span>Total Items: {projects.length + milestones.length}</span>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <span> Projects: {projects.length}</span>
            <span>📋 Project-tasks: {milestones.length}</span>
          </div>
        </div>
      </div>
      <div
        ref={ganttRef}
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflowX: 'auto',
          minHeight: '400px'
        }}
      ></div>
      {tasks.length === 0 && !loading && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center',
          color: '#6b7280',
          marginTop: '1rem'
        }}>
          No projects or project-tasks found. Create some projects and project-tasks to see them in the timeline.
        </div>
      )}
    </div>
  );
};

export default GanttChart;


